


import { makeStyles } from 'tss-react/mui';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  KeyValue,
  MUIDataTable,
  MUIDataTableColumnDef,
  Pill,
  Typography,
} from 'web-library';
import { ResitCandidateApplicationStatus } from '../../../../api/candidate/resit/@coordinator/model';
import FormHeader from '../../../../components/form-items/FormHeader';
import SubjectFeeDetail from '../../../../components/SubjectFeeDetail';
import { GroupResitSubjectRow } from '../../../../models/@candidate/candidateResitRegistrationSchema';
import {CSSInterpolation, GlobalStyles} from "tss-react";
import {Theme, useTheme} from "@mui/material";
const globalStyle = (_theme: Theme): CSSInterpolation => ({
  '#subject-review-candidate-table': {
    width: '100%',
  },
  '#subject-review-candidate-table .MuiTableCell-root': {
    '&[data-colindex="5"]': {
      width: 200,
    },
    '&[data-colindex="6"]': {
      width: 200,
    },
  },
  '#subject-review-candidate-table .MuiTableCell-head': {
    '&[data-colindex="5"]': {
      textAlign: 'right',
    },
    '&[data-colindex="6"]': {
      textAlign: 'right',
    },
    '&[data-colindex="7"]': {
      textAlign: 'right',
    },
  },
})
const useStyles = makeStyles()((theme) =>
({
  feeCell: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  subtitle: {
    marginBottom: theme.spacing(4),
  },
}));

export interface SelectedSubjectsCandidatesReviewProps {
  hideStatus?: boolean;
  examFee?: boolean;

  noOfCandidate?: number;
  examYear?: string;
  examLevel?: string;

  candidateSubjects: ResitCandidateApplicationStatus[];
  totalExamFee: string;
  totalFee: string;
  totalGst: string;
  totalPayable: string;
  gstAbsorbed: string;

  enabledPayment?: boolean;
}

const useSubjectInfoWithCandidates = (candidateSubjects: ResitCandidateApplicationStatus[]) =>
  useMemo(
    () =>
      candidateSubjects.reduce((accumulator: GroupResitSubjectRow[], currentValue, reduceIndex) => {
        currentValue.subjects.forEach((sub, idx) => {
          accumulator.push({
            id: `${reduceIndex}-${idx}`,
            statutoryName: currentValue.statutoryName,
            nricFin: currentValue.nricFin,
            ...sub,
          });
        });
        return accumulator;
      }, []),
    [candidateSubjects]
  );

const SubjectReviewCandidatesValue = ({
  classes,
  isFeeCell,
  value,
}: {
  classes: Record<string,string>;
  isFeeCell: boolean;
  value: string;
}) => (
  <Box className={isFeeCell ? classes.feeCell : ''}>
    <Typography variant="body1">{value}</Typography>{' '}
  </Box>
);

const RegistrationStatus = ({
  classes,
  registrationStatus,
}: {
  classes: Record<string,string>;
  registrationStatus: string;
}) => (
  <Box className={classes.feeCell}>
    <Pill
      variant={registrationStatus === 'Registered' ? 'positive' : 'info'}
      label={registrationStatus}
    />
  </Box>
);

const SelectedSubjectsCandidatesReview = (props: SelectedSubjectsCandidatesReviewProps) => {
  const {
    hideStatus = false,
    examFee = false,
    noOfCandidate,
    examYear,
    examLevel,

    candidateSubjects,
    totalExamFee = '0',
    totalFee = '0',
    totalGst = '0',
    totalPayable = '0',
    gstAbsorbed = '0.00',
    enabledPayment = true,
  } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  const theme = useTheme();
  const constructSubjectInfoWithCandidates = useSubjectInfoWithCandidates(candidateSubjects);

  /** Datagrid columns
   * */
  const columns: MUIDataTableColumnDef[] = useMemo(() => {
    const tempColumn = [
      {
        name: 'statutoryName',
        label: t('components->>SelectedSubjectReview.Candidate'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SubjectReviewCandidatesValue({
              classes,
              isFeeCell: false,
              value: constructSubjectInfoWithCandidates[dataIndex].statutoryName,
            }),
        },
      },
      {
        name: 'nricFin',
        label: t('components->>SelectedSubjectReview.NRIC'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SubjectReviewCandidatesValue({
              classes,
              isFeeCell: false,
              value: constructSubjectInfoWithCandidates[dataIndex].nricFin,
            }),
        },
      },
      {
        name: 'subject',
        label: t('components->>SelectedSubjectReview.Subject Name'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SubjectReviewCandidatesValue({
              classes,
              isFeeCell: false,
              value: constructSubjectInfoWithCandidates[dataIndex].subject,
            }),
        },
      },
      {
        name: 'subjectCode',
        label: t('components->>SelectedSubjectReview.Subject Code'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SubjectReviewCandidatesValue({
              classes,
              isFeeCell: false,
              value: constructSubjectInfoWithCandidates[dataIndex].subjectCode,
            }),
        },
      },
      {
        name: 'examSeries',
        label: t('components->>SelectedSubjectReview.Exam Series'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SubjectReviewCandidatesValue({
              classes,
              isFeeCell: false,
              value: constructSubjectInfoWithCandidates[dataIndex].examSeries,
            }),
        },
      },
      {
        name: 'examFees',
        label: t('components->>SelectedSubjectReview.Exam Fees (SGD)'),

        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SubjectReviewCandidatesValue({
              classes,
              isFeeCell: true,
              value: constructSubjectInfoWithCandidates[dataIndex].examFees as string,
            }),
        },
      },
      {
        name: 'gst',
        label: t('components->>SelectedSubjectReview.GST'),

        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SubjectReviewCandidatesValue({
              classes,
              isFeeCell: true,
              value: constructSubjectInfoWithCandidates[dataIndex].gst as string,
            }),
        },
      },
    ];
    if (examFee) {
      tempColumn.push({
        name: 'totalSubjectFees',
        label: t('components->>SelectedSubjectReview.ExamFeesPayable'),

        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SubjectReviewCandidatesValue({
              classes,
              isFeeCell: true,
              value: constructSubjectInfoWithCandidates[dataIndex].totalSubjectFees as string,
            }),
        },
      });
    }

    if (!hideStatus) {
      tempColumn.push({
        name: 'registrationStatus',
        label: t('components->>SelectedSubjectReview.Status'),

        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            RegistrationStatus({
              classes,
              registrationStatus: constructSubjectInfoWithCandidates[dataIndex].registrationStatus,
            }),
        },
      });
    }
    return tempColumn;
  }, [classes, constructSubjectInfoWithCandidates, examFee, hideStatus, t]);

  return (
    (<>
      <GlobalStyles
          styles={globalStyle(theme)}/>
          <Grid container alignItems="center" justify="flex-start">
            <Grid xs={12}>
              <FormHeader title="Candidate(s)" />
            </Grid>
            <Grid xs={12}>
              <Typography variant="subtitle1" className={classes.subtitle}>
                Total Candidate(s): {noOfCandidate}
              </Typography>
            </Grid>
            {examYear && examLevel && (
                <Grid container>
                  <Grid xs={6} md={3}>
                    <KeyValue keyText={t('components->>ReceiptDetails.Exam Level')} valueText={examLevel} />
                  </Grid>
                  <Grid xs={6} md={3}>
                    <KeyValue keyText={t('components->>ReceiptDetails.Exam Year')} valueText={examYear} />
                  </Grid>
                </Grid>
            )}
            <Box id="subject-review-candidate-table">
              <MUIDataTable
                  columns={columns}
                  data={constructSubjectInfoWithCandidates}
                  options={[]}
                  components={[]}
              />
            </Box>
            <br />
            {enabledPayment && (
                <Grid xs={12} md={8}>
                  <SubjectFeeDetail
                      totalExamFee={totalExamFee}
                      totalGst={totalGst}
                      totalFee={totalFee}
                      totalPayable={totalPayable}
                      gstAbsorbed={gstAbsorbed}
                  />
                </Grid>
            )}
          </Grid>
    </>
        )
  );
};
export default SelectedSubjectsCandidatesReview;
